function generateRectanglePath(config) {
  const { width, height } = config;
  const path = [
    { cmd: 'moveTo', x: 0, y: 0 },
    { cmd: 'lineTo', x: width, y: 0 },
    { cmd: 'lineTo', x: width, y: height },
    { cmd: 'lineTo', x: 0, y: height },
    { cmd: 'closePath' }
  ];
  return path;
}

/**
 * Генерирует точки пути для отрисовки кусочка пазла на canvas
 * @param {Object} config - Конфигурация кусочка пазла
 * @returns {Object} Данные пути для canvas
 */
export function generatePuzzlePath(config) {
  const { width, height, puzzleTabSize, puzzleJitter, puzzleType, position, grid, edges } = config;

  // Если puzzleTabSize равен 0, генерируем простой прямоугольный путь
  if (puzzleTabSize === 0 || puzzleType === 'square') {
    return generateRectanglePath(config);
  }

  const { row, col } = position;

  if ((col+row) % 2 === 0) {
    return mirrorPuzzlePath(config);
  }
  
  const { rows, cols } = grid;

  // Инициализация генератора случайных чисел
  const randomWithSeed = (localSeed) => {
    const x = Math.sin(localSeed) * 10000;
    return x - Math.floor(x);
  };

  const uniform = (min, max, s) => min + randomWithSeed(s) * (max - min);

  const tabScaleFactor = 0.65;
  const t = (puzzleTabSize / 200.0) * tabScaleFactor;
  const j = puzzleJitter / 100.0;

  const path = [];

  let flip, a, b, c, d, e;
  const initParams = (edgeSeed) => {
    e = uniform(-j, j, edgeSeed + 1);
    a = e;
    b = uniform(-j, j, edgeSeed + 2);
    c = uniform(-j, j, edgeSeed + 3);
    d = uniform(-j, j, edgeSeed + 4);
  };

  path.push({ cmd: 'moveTo', x: 0, y: 0 });

  // Верхняя сторона
  if (row > 0) {
    const edgeId = `h_${row - 1}_${col}`;
    const { seed: edgeSeed, direction } = edges.horizontal[edgeId];
    flip = direction === 'down'; // Выступ вниз = впадина сверху
    initParams(edgeSeed);

    path.push({ cmd: 'lineTo', x: 0.2 * width, y: 0 });
    if (!flip) {
      // Впадина
      path.push({ cmd: 'bezierCurveTo',
        cp1x: (0.2 + a * 0.1) * width, cp1y: 0,
        cp2x: (0.5 + b + d) * width, cp2y: (-t + c) * height,
        x: (0.5 - t + b) * width, y: (t + c) * height
      });
      path.push({ cmd: 'bezierCurveTo',
        cp1x: (0.5 - 2.0 * t + b - d) * width, cp1y: (3.0 * t + c) * height,
        cp2x: (0.5 + 2.0 * t + b - d) * width, cp2y: (3.0 * t + c) * height,
        x: (0.5 + t + b) * width, y: (t + c) * height
      });
      path.push({ cmd: 'bezierCurveTo',
        cp1x: (0.5 + b + d) * width, cp1y: (-t + c) * height,
        cp2x: (0.8 + e * 0.1) * width, cp2y: 0,
        x: 0.8 * width, y: 0
      });
    } else {
      // Выступ
      path.push({ cmd: 'bezierCurveTo',
        cp1x: (0.2 + a * 0.1) * width, cp1y: 0,
        cp2x: (0.5 + b + d) * width, cp2y: (t + c) * height,
        x: (0.5 - t + b) * width, y: (-t + c) * height
      });
      path.push({ cmd: 'bezierCurveTo',
        cp1x: (0.5 - 2.0 * t + b - d) * width, cp1y: (-3.0 * t + c) * height,
        cp2x: (0.5 + 2.0 * t + b - d) * width, cp2y: (-3.0 * t + c) * height,
        x: (0.5 + t + b) * width, y: (-t + c) * height
      });
      path.push({ cmd: 'bezierCurveTo',
        cp1x: (0.5 + b + d) * width, cp1y: (t + c) * height,
        cp2x: (0.8 + e * 0.1) * width, cp2y: 0,
        x: 0.8 * width, y: 0
      });
    }
    path.push({ cmd: 'lineTo', x: width, y: 0 });
  } else {
    path.push({ cmd: 'lineTo', x: width, y: 0 });
  }

  // Правая сторона
  if (col < cols - 1) {
    const edgeId = `v_${row}_${col}`;
    const { seed: edgeSeed, direction } = edges.vertical[edgeId];
    flip = direction === 'left'; // Выступ влево = впадина справа
    initParams(edgeSeed);

    path.push({ cmd: 'lineTo', x: width, y: 0.2 * height });
    if (!flip) {
      // Впадина
      path.push({ cmd: 'bezierCurveTo',
        cp1x: width, cp1y: (0.2 + a * 0.1) * height,
        cp2x: (width + (-t + c) * width), cp2y: (0.5 + b + d) * height,
        x: (width + (t + c) * width), y: (0.5 - t + b) * height
      });
      path.push({ cmd: 'bezierCurveTo',
        cp1x: (width + (3.0 * t + c) * width), cp1y: (0.5 - 2.0 * t + b - d) * height,
        cp2x: (width + (3.0 * t + c) * width), cp2y: (0.5 + 2.0 * t + b - d) * height,
        x: (width + (t + c) * width), y: (0.5 + t + b) * height
      });
      path.push({ cmd: 'bezierCurveTo',
        cp1x: (width + (-t + c) * width), cp1y: (0.5 + b + d) * height,
        cp2x: width, cp2y: (0.8 + e * 0.1) * height,
        x: width, y: 0.8 * height
      });
    } else {
      // Выступ
      path.push({ cmd: 'bezierCurveTo',
        cp1x: width, cp1y: (0.2 + a * 0.1) * height,
        cp2x: (width + (t + c) * width), cp2y: (0.5 + b + d) * height,
        x: (width + (-t + c) * width), y: (0.5 - t + b) * height
      });
      path.push({ cmd: 'bezierCurveTo',
        cp1x: (width + (-3.0 * t + c) * width), cp1y: (0.5 - 2.0 * t + b - d) * height,
        cp2x: (width + (-3.0 * t + c) * width), cp2y: (0.5 + 2.0 * t + b - d) * height,
        x: (width + (-t + c) * width), y: (0.5 + t + b) * height
      });
      path.push({ cmd: 'bezierCurveTo',
        cp1x: (width + (t + c) * width), cp1y: (0.5 + b + d) * height,
        cp2x: width, cp2y: (0.8 + e * 0.1) * height,
        x: width, y: 0.8 * height
      });
    }
    path.push({ cmd: 'lineTo', x: width, y: height });
  } else {
    path.push({ cmd: 'lineTo', x: width, y: height });
  }

  // Нижняя сторона
  if (row < rows - 1) {
    const edgeId = `h_${row}_${col}`;
    const { seed: edgeSeed, direction } = edges.horizontal[edgeId];
    flip = direction === 'down'; // Выступ вверх = впадина снизу
    initParams(edgeSeed);

    path.push({ cmd: 'lineTo', x: 0.8 * width, y: height });
    if (!flip) {
      // Впадина
      path.push({ cmd: 'bezierCurveTo',
        cp1x: (0.8 - e * 0.1) * width, cp1y: height,
        cp2x: (0.5 - b - d) * width, cp2y: (height + (-t + c) * height),
        x: (0.5 + t - b) * width, y: (height + (t + c) * height)
      });
      path.push({ cmd: 'bezierCurveTo',
        cp1x: (0.5 + 2.0 * t - b + d) * width, cp1y: (height + (3.0 * t + c) * height),
        cp2x: (0.5 - 2.0 * t - b + d) * width, cp2y: (height + (3.0 * t + c) * height),
        x: (0.5 - t - b) * width, y: (height + (t + c) * height)
      });
      path.push({ cmd: 'bezierCurveTo',
        cp1x: (0.5 - b - d) * width, cp1y: (height + (-t + c) * height),
        cp2x: (0.2 - e * 0.1) * width, cp2y: height,
        x: 0.2 * width, y: height
      });
    } else {
      // Выступ
      path.push({ cmd: 'bezierCurveTo',
        cp1x: (0.8 - e * 0.1) * width, cp1y: height,
        cp2x: (0.5 - b - d) * width, cp2y: (height + (t + c) * height),
        x: (0.5 + t - b) * width, y: (height + (-t + c) * height)
      });
      path.push({ cmd: 'bezierCurveTo',
        cp1x: (0.5 + 2.0 * t - b + d) * width, cp1y: (height + (-3.0 * t + c) * height),
        cp2x: (0.5 - 2.0 * t - b + d) * width, cp2y: (height + (-3.0 * t + c) * height),
        x: (0.5 - t - b) * width, y: (height + (-t + c) * height)
      });
      path.push({ cmd: 'bezierCurveTo',
        cp1x: (0.5 - b - d) * width, cp1y: (height + (t + c) * height),
        cp2x: (0.2 - e * 0.1) * width, cp2y: height,
        x: 0.2 * width, y: height
      });
    }
    path.push({ cmd: 'lineTo', x: 0, y: height });
  } else {
    path.push({ cmd: 'lineTo', x: 0, y: height });
  }

  // Левая сторона
  if (col > 0) {
    const edgeId = `v_${row}_${col - 1}`;
    const { seed: edgeSeed, direction } = edges.vertical[edgeId];
    flip = direction === 'left'; // Выступ вправо = впадина слева
    initParams(edgeSeed);

    path.push({ cmd: 'lineTo', x: 0, y: 0.8 * height });
    if (!flip) {
      // Впадина
      path.push({ cmd: 'bezierCurveTo',
        cp1x: 0, cp1y: (0.8 - e * 0.1) * height,
        cp2x: ((-t + c) * width), cp2y: (0.5 - b - d) * height,
        x: ((t + c) * width), y: (0.5 + t - b) * height
      });
      path.push({ cmd: 'bezierCurveTo',
        cp1x: ((3.0 * t + c) * width), cp1y: (0.5 + 2.0 * t - b + d) * height,
        cp2x: ((3.0 * t + c) * width), cp2y: (0.5 - 2.0 * t - b + d) * height,
        x: ((t + c) * width), y: (0.5 - t - b) * height
      });
      path.push({ cmd: 'bezierCurveTo',
        cp1x: ((-t + c) * width), cp1y: (0.5 - b - d) * height,
        cp2x: 0, cp2y: (0.2 - e * 0.1) * height,
        x: 0, y: 0.2 * height
      });
    } else {
      // Выступ
      path.push({ cmd: 'bezierCurveTo',
        cp1x: 0, cp1y: (0.8 - e * 0.1) * height,
        cp2x: ((t + c) * width), cp2y: (0.5 - b - d) * height,
        x: ((-t + c) * width), y: (0.5 + t - b) * height
      });
      path.push({ cmd: 'bezierCurveTo',
        cp1x: ((-3.0 * t + c) * width), cp1y: (0.5 + 2.0 * t - b + d) * height,
        cp2x: ((-3.0 * t + c) * width), cp2y: (0.5 - 2.0 * t - b + d) * height,
        x: ((-t + c) * width), y: (0.5 - t - b) * height
      });
      path.push({ cmd: 'bezierCurveTo',
        cp1x: ((t + c) * width), cp1y: (0.5 - b - d) * height,
        cp2x: 0, cp2y: (0.2 - e * 0.1) * height,
        x: 0, y: 0.2 * height
      });
    }
    path.push({ cmd: 'lineTo', x: 0, y: 0 });
  } else {
    path.push({ cmd: 'lineTo', x: 0, y: 0 });
  }

  path.push({ cmd: 'closePath' });

  return path;
}


export function mirrorPuzzlePath(config) {
  const { width, height, puzzleTabSize, puzzleJitter, position, grid, edges } = config;
  const { row, col } = position;
  const { rows, cols } = grid;

  const randomWithSeed = (localSeed) => {
    const x = Math.sin(localSeed) * 10000;
    return x - Math.floor(x);
  };

  const uniform = (min, max, s) => min + randomWithSeed(s) * (max - min);

  const tabScaleFactor = 0.65;
  const t = (puzzleTabSize / 200.0) * tabScaleFactor;
  const j = puzzleJitter / 100.0;

  const path = [];

  let flip, a, b, c, d, e;
  const initParams = (edgeSeed) => {
    e = uniform(-j, j, edgeSeed + 1);
    a = e;
    b = uniform(-j, j, edgeSeed + 2);
    c = uniform(-j, j, edgeSeed + 3);
    d = uniform(-j, j, edgeSeed + 4);
  };

  // Начальная точка теперь в левом верхнем углу
  path.push({ cmd: 'moveTo', x: 0, y: 0 });

  // Левая сторона (сверху вниз)
  if (col > 0) {
    const edgeId = `v_${row}_${col - 1}`;
    const { seed: edgeSeed, direction } = edges.vertical[edgeId];
    flip = direction === 'left'; // Выступ вправо = впадина слева
    initParams(edgeSeed);

    path.push({ cmd: 'lineTo', x: 0, y: 0.2 * height });
    if (!flip) {
      // Впадина
      path.push({ cmd: 'bezierCurveTo',
        cp1x: 0, cp1y: (0.2 + a * 0.1) * height,
        cp2x: ((-t + c) * width), cp2y: (0.5 + b + d) * height,
        x: ((t + c) * width), y: (0.5 - t + b) * height
      });
      path.push({ cmd: 'bezierCurveTo',
        cp1x: ((3.0 * t + c) * width), cp1y: (0.5 - 2.0 * t + b - d) * height,
        cp2x: ((3.0 * t + c) * width), cp2y: (0.5 + 2.0 * t + b - d) * height,
        x: ((t + c) * width), y: (0.5 + t + b) * height
      });
      path.push({ cmd: 'bezierCurveTo',
        cp1x: ((-t + c) * width), cp1y: (0.5 + b + d) * height,
        cp2x: 0, cp2y: (0.8 + e * 0.1) * height,
        x: 0, y: 0.8 * height
      });
    } else {
      // Выступ
      path.push({ cmd: 'bezierCurveTo',
        cp1x: 0, cp1y: (0.2 + a * 0.1) * height,
        cp2x: ((t + c) * width), cp2y: (0.5 + b + d) * height,
        x: ((-t + c) * width), y: (0.5 - t + b) * height
      });
      path.push({ cmd: 'bezierCurveTo',
        cp1x: ((-3.0 * t + c) * width), cp1y: (0.5 - 2.0 * t + b - d) * height,
        cp2x: ((-3.0 * t + c) * width), cp2y: (0.5 + 2.0 * t + b - d) * height,
        x: ((-t + c) * width), y: (0.5 + t + b) * height
      });
      path.push({ cmd: 'bezierCurveTo',
        cp1x: ((t + c) * width), cp1y: (0.5 + b + d) * height,
        cp2x: 0, cp2y: (0.8 + e * 0.1) * height,
        x: 0, y: 0.8 * height
      });
    }
    path.push({ cmd: 'lineTo', x: 0, y: height });
  } else {
    path.push({ cmd: 'lineTo', x: 0, y: height });
  }

  // Нижняя сторона (слева направо)
  if (row < rows - 1) {
    const edgeId = `h_${row}_${col}`;
    const { seed: edgeSeed, direction } = edges.horizontal[edgeId];
    flip = direction === 'down'; // Выступ вверх = впадина снизу
    initParams(edgeSeed);

    path.push({ cmd: 'lineTo', x: 0.2 * width, y: height });
    if (!flip) {
      // Впадина
      path.push({ cmd: 'bezierCurveTo',
        cp1x: (0.2 + a * 0.1) * width, cp1y: height,
        cp2x: (0.5 + b + d) * width, cp2y: (height + (-t + c) * height),
        x: (0.5 - t + b) * width, y: (height + (t + c) * height)
      });
      path.push({ cmd: 'bezierCurveTo',
        cp1x: (0.5 - 2.0 * t + b - d) * width, cp1y: (height + (3.0 * t + c) * height),
        cp2x: (0.5 + 2.0 * t + b - d) * width, cp2y: (height + (3.0 * t + c) * height),
        x: (0.5 + t + b) * width, y: (height + (t + c) * height)
      });
      path.push({ cmd: 'bezierCurveTo',
        cp1x: (0.5 + b + d) * width, cp1y: (height + (-t + c) * height),
        cp2x: (0.8 + e * 0.1) * width, cp2y: height,
        x: 0.8 * width, y: height
      });
    } else {
      // Выступ
      path.push({ cmd: 'bezierCurveTo',
        cp1x: (0.2 + a * 0.1) * width, cp1y: height,
        cp2x: (0.5 + b + d) * width, cp2y: (height + (t + c) * height),
        x: (0.5 - t + b) * width, y: (height + (-t + c) * height)
      });
      path.push({ cmd: 'bezierCurveTo',
        cp1x: (0.5 - 2.0 * t + b - d) * width, cp1y: (height + (-3.0 * t + c) * height),
        cp2x: (0.5 + 2.0 * t + b - d) * width, cp2y: (height + (-3.0 * t + c) * height),
        x: (0.5 + t + b) * width, y: (height + (-t + c) * height)
      });
      path.push({ cmd: 'bezierCurveTo',
        cp1x: (0.5 + b + d) * width, cp1y: (height + (t + c) * height),
        cp2x: (0.8 + e * 0.1) * width, cp2y: height,
        x: 0.8 * width, y: height
      });
    }
    path.push({ cmd: 'lineTo', x: width, y: height });
  } else {
    path.push({ cmd: 'lineTo', x: width, y: height });
  }

  // Правая сторона (снизу вверх)
  if (col < cols - 1) {
    const edgeId = `v_${row}_${col}`;
    const { seed: edgeSeed, direction } = edges.vertical[edgeId];
    flip = direction === 'left'; // Выступ влево = впадина справа
    initParams(edgeSeed);

    path.push({ cmd: 'lineTo', x: width, y: 0.8 * height });
    if (!flip) {
      // Впадина
      path.push({ cmd: 'bezierCurveTo',
        cp1x: width, cp1y: (0.8 - e * 0.1) * height,
        cp2x: (width + (-t + c) * width), cp2y: (0.5 - b - d) * height,
        x: (width + (t + c) * width), y: (0.5 + t - b) * height
      });
      path.push({ cmd: 'bezierCurveTo',
        cp1x: (width + (3.0 * t + c) * width), cp1y: (0.5 + 2.0 * t - b + d) * height,
        cp2x: (width + (3.0 * t + c) * width), cp2y: (0.5 - 2.0 * t - b + d) * height,
        x: (width + (t + c) * width), y: (0.5 - t - b) * height
      });
      path.push({ cmd: 'bezierCurveTo',
        cp1x: (width + (-t + c) * width), cp1y: (0.5 - b - d) * height,
        cp2x: width, cp2y: (0.2 - e * 0.1) * height,
        x: width, y: 0.2 * height
      });
    } else {
      // Выступ
      path.push({ cmd: 'bezierCurveTo',
        cp1x: width, cp1y: (0.8 - e * 0.1) * height,
        cp2x: (width + (t + c) * width), cp2y: (0.5 - b - d) * height,
        x: (width + (-t + c) * width), y: (0.5 + t - b) * height
      });
      path.push({ cmd: 'bezierCurveTo',
        cp1x: (width + (-3.0 * t + c) * width), cp1y: (0.5 + 2.0 * t - b + d) * height,
        cp2x: (width + (-3.0 * t + c) * width), cp2y: (0.5 - 2.0 * t - b + d) * height,
        x: (width + (-t + c) * width), y: (0.5 - t - b) * height
      });
      path.push({ cmd: 'bezierCurveTo',
        cp1x: (width + (t + c) * width), cp1y: (0.5 - b - d) * height,
        cp2x: width, cp2y: (0.2 - e * 0.1) * height,
        x: width, y: 0.2 * height
      });
    }
    path.push({ cmd: 'lineTo', x: width, y: 0 });
  } else {
    path.push({ cmd: 'lineTo', x: width, y: 0 });
  }

  // Верхняя сторона (справа налево)
  if (row > 0) {
    const edgeId = `h_${row - 1}_${col}`;
    const { seed: edgeSeed, direction } = edges.horizontal[edgeId];
    flip = direction === 'down'; // Выступ вниз = впадина сверху
    initParams(edgeSeed);

    path.push({ cmd: 'lineTo', x: 0.8 * width, y: 0 });
    if (!flip) {
      // Впадина
      path.push({ cmd: 'bezierCurveTo',
        cp1x: (0.8 - e * 0.1) * width, cp1y: 0,
        cp2x: (0.5 - b - d) * width, cp2y: (-t + c) * height,
        x: (0.5 + t - b) * width, y: (t + c) * height
      });
      path.push({ cmd: 'bezierCurveTo',
        cp1x: (0.5 + 2.0 * t - b + d) * width, cp1y: (3.0 * t + c) * height,
        cp2x: (0.5 - 2.0 * t - b + d) * width, cp2y: (3.0 * t + c) * height,
        x: (0.5 - t - b) * width, y: (t + c) * height
      });
      path.push({ cmd: 'bezierCurveTo',
        cp1x: (0.5 - b - d) * width, cp1y: (-t + c) * height,
        cp2x: (0.2 - e * 0.1) * width, cp2y: 0,
        x: 0.2 * width, y: 0
      });
    } else {
      // Выступ
      path.push({ cmd: 'bezierCurveTo',
        cp1x: (0.8 - e * 0.1) * width, cp1y: 0,
        cp2x: (0.5 - b - d) * width, cp2y: (t + c) * height,
        x: (0.5 + t - b) * width, y: (-t + c) * height
      });
      path.push({ cmd: 'bezierCurveTo',
        cp1x: (0.5 + 2.0 * t - b + d) * width, cp1y: (-3.0 * t + c) * height,
        cp2x: (0.5 - 2.0 * t - b + d) * width, cp2y: (-3.0 * t + c) * height,
        x: (0.5 - t - b) * width, y: (-t + c) * height
      });
      path.push({ cmd: 'bezierCurveTo',
        cp1x: (0.5 - b - d) * width, cp1y: (t + c) * height,
        cp2x: (0.2 - e * 0.1) * width, cp2y: 0,
        x: 0.2 * width, y: 0
      });
    }
    path.push({ cmd: 'lineTo', x: 0, y: 0 });
  } else {
    path.push({ cmd: 'lineTo', x: 0, y: 0 });
  }

  path.push({ cmd: 'closePath' });

  return path;
}

/**
 * Отрисовывает путь кусочка пазла на заданном контексте Canvas
 * @param {CanvasRenderingContext2D} ctx - Контекст для рисования
 * @param {Array} path - Массив команд пути
 * @param {Object} options - Опции отрисовки
 */
export function drawPuzzlePath(ctx, path, options = {}) {
  const { stroke, fill, lineWidth = 1 } = options;

  ctx.beginPath();

  path.forEach(cmd => {
    switch (cmd.cmd) {
      case 'moveTo':
        ctx.moveTo(cmd.x, cmd.y);
        break;
      case 'lineTo':
        ctx.lineTo(cmd.x, cmd.y);
        break;
      case 'bezierCurveTo':
        ctx.bezierCurveTo(cmd.cp1x, cmd.cp1y, cmd.cp2x, cmd.cp2y, cmd.x, cmd.y);
        break;
      case 'closePath':
        ctx.closePath();
        break;
    }
  });

  if (fill) {
    ctx.fillStyle = fill;
    ctx.fill();
  }

  if (stroke) {
    ctx.strokeStyle = stroke;
    ctx.lineWidth = lineWidth;
    ctx.stroke();
  }
}